import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'

import type { AddFavoriteParams } from '../dal/favorite-dal'
import {
  addFavorite,
  fetchFavorites,
  removeFavorite,
} from '../dal/favorite-dal'
import { queryKeys } from './query-keys'

export type { AddFavoriteParams } from '../dal/favorite-dal'

export const favoritesQueryOptions = queryOptions({
  queryKey: queryKeys.favorites.all,
  queryFn: fetchFavorites,
  staleTime: 2 * 60 * 1000,
})

function useAddFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.favorites.all })
    },
    onError: (error) => {
      console.error('Add favorite failed:', error)
    },
  })
}

function useRemoveFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.favorites.all })
    },
    onError: (error) => {
      console.error('Remove favorite failed:', error)
    },
  })
}

export function useToggleFavorite() {
  const { data: favorites } = useSuspenseQuery(favoritesQueryOptions)
  const addFavoriteMutation = useAddFavorite()
  const removeFavoriteMutation = useRemoveFavorite()

  const toggleFavorite = (
    contentfulId: string,
    favoriteItem: AddFavoriteParams,
  ) => {
    const isFavorited = favorites?.some(
      (fav) => fav.contentfulId === contentfulId,
    )

    if (isFavorited) {
      return removeFavoriteMutation.mutateAsync(contentfulId)
    } else {
      return addFavoriteMutation.mutateAsync(favoriteItem)
    }
  }

  return {
    favorites,
    toggleFavorite,
    isLoading:
      addFavoriteMutation.isPending || removeFavoriteMutation.isPending,
  }
}
